import { createContext, useContext, useState, useCallback, useEffect } from "react";

// Base currency for ALL stored/billed amounts is XCD (East Caribbean Dollar).
// XCD and BBD are both fixed-pegged to the US Dollar, so we use accurate fixed rates.
//   US$1 = XCD 2.70   |   US$1 = BBD 2.00
// The switcher only affects DISPLAYED prices. Actual billing/invoices remain in XCD.
export const CURRENCIES = [
  { code: "XCD", symbol: "XCD $", label: "XCD · East Caribbean $", rate: 1 },
  { code: "USD", symbol: "US$", label: "USD · US Dollar", rate: 1 / 2.7 },
  { code: "BBD", symbol: "Bds$", label: "BBD · Barbados $", rate: 2 / 2.7 },
];

const STORAGE_KEY = "protocall_currency";
const FX_KEY = "protocall_fx";
const FX_MAX_AGE = 12 * 3600 * 1000; // use cached rates for up to 12h before refreshing
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed pegs used until live rates load / if the network fails.
const FALLBACK_RATES = { XCD: 1, USD: 1 / 2.7, BBD: 2 / 2.7 };

// OECS members use the East Caribbean Dollar (XCD).
const XCD_COUNTRIES = ["LC", "AG", "DM", "GD", "MS", "KN", "VC", "AI", "VG"];

// Map an ISO country code to the best display currency for that visitor.
const countryToCurrency = (cc) => {
  if (!cc) return "XCD";
  const c = cc.toUpperCase();
  if (c === "BB") return "BBD"; // Barbados
  if (XCD_COUNTRIES.includes(c)) return "XCD"; // OECS islands
  return "USD"; // international visitors default to USD
};

// Detect the visitor's country via keyless, CORS-friendly IP lookups (with fallback).
const detectCountry = async () => {
  const withTimeout = (p, ms = 3500) =>
    Promise.race([p, new Promise((_, r) => setTimeout(() => r(new Error("timeout")), ms))]);
  try {
    const res = await withTimeout(fetch("https://api.country.is/"));
    if (res.ok) {
      const data = await res.json();
      if (data?.country) return data.country;
    }
  } catch (e) {
    /* try fallback */
  }
  try {
    const res = await withTimeout(fetch("https://ipapi.co/country/"));
    if (res.ok) {
      const text = (await res.text()).trim();
      if (/^[A-Za-z]{2}$/.test(text)) return text;
    }
  } catch (e) {
    /* give up silently */
  }
  return null;
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    const saved = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return CURRENCIES.some((c) => c.code === saved) ? saved : "XCD";
  });
  const [autoDetected, setAutoDetected] = useState(false);
  const [rates, setRates] = useState(() => {
    try {
      const cached = JSON.parse(localStorage.getItem(FX_KEY) || "null");
      if (cached?.rates?.XCD) return cached.rates;
    } catch (e) {
      /* ignore */
    }
    return FALLBACK_RATES;
  });

  const setCurrency = useCallback((next) => {
    if (CURRENCIES.some((c) => c.code === next)) {
      setCode(next);
      localStorage.setItem(STORAGE_KEY, next);
    }
  }, []);

  // On the very first visit (no saved preference), auto-detect currency from location.
  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return; // respect an existing / manual choice
    let cancelled = false;
    (async () => {
      const cc = await detectCountry();
      if (cancelled || localStorage.getItem(STORAGE_KEY)) return; // user may have picked meanwhile
      const detected = countryToCurrency(cc);
      setCode(detected);
      setAutoDetected(true);
      localStorage.setItem(STORAGE_KEY, detected);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch live exchange rates (proxied via our backend, cached client-side for 12h).
  useEffect(() => {
    let cancelled = false;
    try {
      const cached = JSON.parse(localStorage.getItem(FX_KEY) || "null");
      if (cached?.ts && Date.now() - cached.ts < FX_MAX_AGE && cached?.rates?.XCD) return;
    } catch (e) {
      /* ignore */
    }
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/exchange-rates`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || !data?.rates?.XCD) return;
        setRates(data.rates);
        localStorage.setItem(FX_KEY, JSON.stringify({ rates: data.rates, ts: Date.now(), source: data.source }));
      } catch (e) {
        /* keep fallback / cached rates */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const active = CURRENCIES.find((c) => c.code === code) || CURRENCIES[0];
  const rate = rates[active.code] ?? active.rate;

  // Convert an XCD amount to the active currency numeric value.
  const convert = useCallback((xcdAmount) => (Number(xcdAmount) || 0) * rate, [rate]);

  // Format an XCD amount as a display string in the active currency.
  const formatMoney = useCallback(
    (xcdAmount, { decimals = 2 } = {}) => {
      const value = (Number(xcdAmount) || 0) * rate;
      const formatted = value.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });
      return `${active.symbol}${formatted}`;
    },
    [rate, active]
  );

  // Always format in XCD, regardless of the active display currency (for billing disclosure).
  const formatXCD = useCallback((xcdAmount, { decimals = 2 } = {}) => {
    const formatted = (Number(xcdAmount) || 0).toLocaleString("en-US", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return `XCD $${formatted}`;
  }, []);

  return (
    <CurrencyContext.Provider
      value={{ code, currency: active, currencies: CURRENCIES, setCurrency, convert, formatMoney, formatXCD, autoDetected, isXCD: code === "XCD" }}
    >
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used within a CurrencyProvider");
  return ctx;
};

export default CurrencyContext;
